import React, { useEffect } from "react";
import moment from "moment-timezone";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";

import { CircularProgress, CopyToClipboard, PaginationWithLimit } from "../../../../components/ui";
import { userDiscountActions } from "../../../../reducers/userDiscount";

import usePagination from "../../../../hooks/usePagination";
import { APIDiscounts } from "../../../../api";
import { StyledTable } from "../../../../useGeneralStyles";
import { DATE_FORMAT_hhmmAzz } from "../../../../constants";

const AssignedDiscountCodes = (): React.ReactElement => {
  const dispatch = useDispatch();
  const params: { id: string } = useParams();
  const id = +params.id;
  const { limit, setLimit, page, setPage } = usePagination();
  const { loading, data } = useSelector((state) => state.userDiscountCodes);
  const codes = data[id]?.codes || [];
  const count = data[id]?.count;

  useEffect(() => {
    if (+limit === 0) return;

    (async () => {
      dispatch(userDiscountActions.fetchRequest());
      try {
        const response = await APIDiscounts.getCustomerDiscountCodesRequest({
          id,
          limit,
          page,
        });
        dispatch(userDiscountActions.fetchSuccess({ discountCodes: response.data, id }));
      } catch (error) {
        console.error("error loading user discount codes", error);
        dispatch(userDiscountActions.fetchFailed());
      }
    })();
  }, [page, limit]);

  return (
    <Box mb="15px">
      <Typography fontWeight={600}>Discount codes</Typography>
      <CircularProgress isLoading={loading} />

      {!loading && !codes.length && <span>No discount codes assigned</span>}

      {!loading && !!codes.length && (
        <div>
          <StyledTable tableWithoutHover>
            <thead>
              <tr>
                <td>Code</td>
                <td>Plan</td>
                <td>Used</td>
                <td>Created</td>
              </tr>
            </thead>

            <tbody>
              {codes.map(({ code, name, purchase_date, created, index_row }) => (
                <tr key={index_row} data-test="discount-code-row">
                  <td>
                    <CopyToClipboard value={code} />
                  </td>
                  <td data-test="plan-name">{name}</td>
                  <td data-test="purchase-date">
                    {purchase_date &&
                      moment(purchase_date).tz("America/Chicago").format(DATE_FORMAT_hhmmAzz)}
                  </td>
                  <td data-test="created-date">
                    {created && moment(created).tz("America/Chicago").format(DATE_FORMAT_hhmmAzz)}
                  </td>
                </tr>
              ))}
            </tbody>
          </StyledTable>

          <PaginationWithLimit
            count={count}
            limit={limit}
            page={page}
            setPageState={setPage}
            setLimit={setLimit}
          />
        </div>
      )}
    </Box>
  );
};

export default AssignedDiscountCodes;
